import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSetAtom } from 'jotai';
import find from 'lodash/find';
import { Select, Button, Form, Divider, message, Tooltip, Tag } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import useKpi from '@hooks/useKPI';
import { refreshTrends } from '@store/common/actions';
import { clearCache } from '@src/api/cacheAdapter/config';
import { SYSTEM_DEFINED_SPI_IDS } from '@src/utils/kpi';
import { isAdminRoleSelector } from '@store/common/selectors';
import kpiStore from '@src/globalStore/kpiStore';
import { editModeAtom, selectedKpiThresholdsAtom } from '../atoms';
import FieldElement from './FieldElement';
import { getKpiSelectOptions } from '../utils';
import DeleteModal from './DeleteModal';
import {
  StyledForm,
  StyledKPIWrapper,
  StyledBody,
  StyledAddButton,
  StyledFooter,
  StyledEditButton,
  StyledDeleteButton,
  StyledTop,
  StyledOption,
  StyledCaption,
} from '../styled';
import { ALLOWED_KPIS_NUMBER, LIMIT_TEXT, CONVIVA_MODE_TEXT } from '@src/utils/kpi';


const { Option } = Select;

type kpiOptionsType = {
  label: string;
  value: string;
};

const ViewPanel = () => {
  const dispatch = useDispatch();
  const isAdmin = useSelector(isAdminRoleSelector);
  const panelRef: any = useRef(null);
  const [isApplyLoading, setIsApplyLoading] = useState<boolean>(false);
  const kpiList = kpiStore((state: any) => state.kpiList);
  const currentKpi = kpiStore((state: any) => state.currentKpi);
  const setIsKpiDrawerOpen = kpiStore((state: any) => state.setIsKpiDrawerOpen);
  const kpiMetaConfigs = kpiStore((state: any) => state.kpiMetaConfigs);
  const setEditMode = useSetAtom(editModeAtom);
  const { applyKpiMutation } = useKpi();
  const [deleteMode, setDeleteMode] = useState(false);
  const [convivaMode, setConvivaMode] = useState(false);
  const setSelectedKpiThresholds = useSetAtom(selectedKpiThresholdsAtom);
  const [kpiSelectOptions, setKpiSelectOptions] = useState<kpiOptionsType[]>([]);
  const [kpiValues, setKpiValues] = useState<any>({});

  const onKpiChange = (kpiId) => {
    const kpiDetails = find(kpiList, { kpiID: kpiId });
    if (kpiDetails) {
      setKpiValues(kpiDetails);
    }
  };

  useEffect(() => {
    const options: kpiOptionsType[] = getKpiSelectOptions(kpiList);
    setKpiSelectOptions(options);
    onKpiChange(currentKpi?.kpiID);
  }, [kpiList, currentKpi]);

  useEffect(() => {
    if (SYSTEM_DEFINED_SPI_IDS.includes(kpiValues.kpiID)) {
      setConvivaMode(true);
    } else {
      setConvivaMode(false);
    }
  }, [kpiValues]);

  const onCancelHandler = () => {
    setIsKpiDrawerOpen(false);
  };

  const onApplyHandler = () => {
    setIsApplyLoading(true);
    if (kpiValues.kpiID !== currentKpi?.kpiID) {
      applyKpiMutation.mutate(kpiValues.kpiID, {
        onSuccess: () => {
          message.info(`${kpiValues.kpiName} KPI has been applied successfully.`);
          setIsApplyLoading(false);
          clearCache(); // clear axios cache as threshold changed
          dispatch(refreshTrends());
          setIsKpiDrawerOpen(false);
        },
        onError: () => {
          message.error('There was an error applying KPI. Please try again later.');
          setIsApplyLoading(false);
        },
      });
    } else {
      setIsKpiDrawerOpen(false);
    }
  };

  const onEditKpiHandler = () => {
    setSelectedKpiThresholds(kpiValues);
    setEditMode(true);
  };

  const createNewKpi = () => {
    setEditMode(true);
  };

  return (
    <>
      <StyledBody>
        <StyledKPIWrapper ref={panelRef}>
          <StyledTop>
            <p>Select KPI</p>
          </StyledTop>
          <div>
            <Select
              value={kpiValues.kpiID}
              style={{ width: 250 }}
              onChange={(value) => onKpiChange(value)}
            >
              {kpiSelectOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  <StyledOption>
                    <span>{option.label}</span>
                    {SYSTEM_DEFINED_SPI_IDS.includes(option.value) && <Tag>System</Tag>}
                  </StyledOption>
                </Option>
              ))}
            </Select>
            <Tooltip title={convivaMode && CONVIVA_MODE_TEXT}>
              <StyledEditButton
                onClick={() => onEditKpiHandler()}
                disabled={convivaMode || !isAdmin}
              >
                <EditOutlined />
                Edit
              </StyledEditButton>
            </Tooltip>
            <Tooltip title={convivaMode && CONVIVA_MODE_TEXT}>
              <StyledDeleteButton
                onClick={() => setDeleteMode(true)}
                disabled={convivaMode || !isAdmin}
              >
                <DeleteOutlined />
                Delete
              </StyledDeleteButton>
            </Tooltip>
            <Divider type="vertical" />
            <Tooltip
              title={kpiList.length > ALLOWED_KPIS_NUMBER ? LIMIT_TEXT : 'Create New KPI'}
              placement="topRight"
            >
              <StyledAddButton
                onClick={() => createNewKpi()}
                disabled={kpiList.length > ALLOWED_KPIS_NUMBER || !isAdmin}
              >
                + Add New
              </StyledAddButton>
            </Tooltip>
            <DeleteModal
              currentKpi={currentKpi}
              kpiValues={kpiValues}
              isModalOpen={deleteMode}
              discardDeleteHandler={() => setDeleteMode(false)}
              panelRef={panelRef}
            ></DeleteModal>
          </div>
        </StyledKPIWrapper>
        <StyledCaption>
          <p>A minute is considered good if</p>
        </StyledCaption>
        <StyledForm>
          <Form layout="horizontal">
            {Object.keys(kpiMetaConfigs).map((thresholdKey, index) => {
              return (
                <FieldElement
                  key={thresholdKey}
                  kpiIndex={index}
                  thresholdKey={thresholdKey}
                  thresholdValue={kpiValues[thresholdKey]}
                />
              );
            })}
          </Form>
        </StyledForm>
      </StyledBody>
      <StyledFooter>
        <Button type="primary" onClick={onCancelHandler} ghost>
          Cancel
        </Button>
        <Button type="primary" onClick={onApplyHandler} loading={isApplyLoading}>
          Apply
        </Button>
      </StyledFooter>
    </>
  );
};

export default ViewPanel;
